import React, { Fragment, useState, useEffect } from 'react';
import { iecWiseEPCGDetailsService } from '../../../services/EPCGReportService';
import { getBRCorelationService, getReferenceDataService } from '../../../services/MasterService';
import { Table, Button, Spin, Alert, DatePicker, message, Select } from 'antd';
import { Col, Row } from 'react-bootstrap';
import { Excel } from 'antd-table-saveas-excel';
import Card from '../../../components/bootstrap/card';
import { getReportSummaryData, getAllAdminIECsService } from '../../../services/FileDataService';
import { getTodaysDate, formatToTwoDecimals } from '../../../utilities/utils';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import dayjs from 'dayjs';
import { useLocation } from 'react-router-dom';

import useColumnVisibility from '../../../hooks/useColumnVisibility';
import ColumnSelector from '../../../components/common/ColumnSelector';

const { RangePicker } = DatePicker;
const EPCGReportComponent = () => {
  const location = useLocation();
  const [jsonData, setJsonData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSummaryLoading, setIsSummaryLoading] = useState(false);
  const [error, setError] = useState(null);
  const [summaryDashboardData, setSummaryDashboardData] = useState([]);
  const [IECOptions, setIECOptions] = useState([]);
  const userRole = localStorage.getItem('role');
  const [selectedIEC, setSelectedIEC] = useState(
    localStorage.getItem('iec_no')
  );
  const [selectedIECName, setSelectedIECName] = useState(localStorage.getItem('name'))
  const today = new Date();
  const day = String(today.getDate()).padStart(2, '0');
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const year = today.getFullYear();
  const formattedDate = `${day}-${month}-${year}`;
  const [isValid, setIsValid] = useState(true); // Validation state
  const [billNo, setBillNo] = useState("");
  const [billNoOptions, setBillNoOptions] = useState([]); // Options for dropdown
  const [selectedBillNo, setSelectedBillNo] = useState(""); // Selected bill number
  const [branchCodeNames, setBranchCodeNames] = useState([]);
  const [selectedBranchCodes, setSelectedBranchCodes] = useState([]);

  const currentYear = dayjs().year();
  const defaultFrom = dayjs(`${currentYear}-01-01`);
  const defaultTo = dayjs(`${currentYear}-12-31`);
  const [dates, setDates] = useState(() => {
    // if report is opened through navigation from dashboard, then use those dates else default
    if (location.state?.passedDates?.length === 2) {
      return [
        dayjs(location.state.passedDates[0]),
        dayjs(location.state.passedDates[1]),
      ];
    }
    return [defaultFrom, defaultTo];
  });

  // ─── TABLE STYLES ────────────────────────────────────────────────────────────
  const tableStyles = `
    .epcg-table .ant-table-thead > tr > th,
    .epcg-summary-table .ant-table-thead > tr > th {
      white-space: normal !important;
      word-break: break-word !important;
      overflow: visible !important;
      text-overflow: unset !important;
      vertical-align: middle !important;
      line-height: 1.3 !important;
      padding-top: 8px !important;
      padding-bottom: 8px !important;
    }
    .epcg-table .ant-table-tbody > tr > td,
    .epcg-summary-table .ant-table-tbody > tr > td {
      white-space: nowrap;
    }
    .epcg-table .ant-table-container,
    .epcg-summary-table .ant-table-container {
      overflow-x: auto !important;
    }
    .epcg-table .ant-table-header,
    .epcg-summary-table .ant-table-header {
      overflow: hidden !important;
    }
    .epcg-table .ant-table-sticky-holder,
    .epcg-summary-table .ant-table-sticky-holder {
      overflow: hidden !important;
    }
  `;


  const columns = [
    { title: 'BUSINESS UNIT', dataIndex: 'business_unit', key: 'business_unit', header: 'BUSINESS UNIT', width: 160 , fixed: 'left' },
    { title: 'PLANT NAME', dataIndex: 'plant_name', key: 'plant_name', header: 'PLANT NAME', width: 150 , fixed: 'left' },
    { title: 'EPCG. AUTH. NO.', dataIndex: 'auth_number', key: 'auth_number', header: 'EPCG. AUTH. NO.', width: 180 },
    {
      title: 'EPCG. AUTH. DT.',
      dataIndex: 'date',
      key: 'date',
      header: 'EPCG. AUTH. DT.',
      width: 170,
      render: (text, record) => record.date ? formatDateToDDMMYY(record.date) : ''
    },
    { title: 'FY', dataIndex: 'fy', key: 'fy', header: 'FY', width: 80 },
    {
      title: 'EO IN INR',
      dataIndex: 'eo_fcc',
      key: 'eo_fcc',
      header: 'EO IN INR',
      align: 'right',
      width: 130,
      render: (text, record) => {
        return formatToTwoDecimals(text)
      }
    },
    { title: 'IEC/BR CODE', dataIndex: 'iec_br', key: 'iec_br', header: 'IEC/BR CODE', width: 150 },
    { title: 'PORT', dataIndex: 'port_code', key: 'port_code', header: 'PORT', width: 90 },
    { title: 'REF. CODE', dataIndex: 'reference_code', key: 'reference_code', header: 'REF. CODE', width: 125 },
    { title: 'SHB NO.', dataIndex: 'sb_no', key: 'sb_no', header: 'SHB NO.', width: 115 },
    { title: 'SHB DT.', dataIndex: 'sb_date', key: 'sb_date', header: 'SHB DT.', width: 110 },
    { title: 'HSN CODE', dataIndex: 'hs_cd', key: 'hs_cd', header: 'HSN CODE', width: 115 },
    { title: 'DESCRIPTION', dataIndex: 'description', key: 'description', header: 'DESCRIPTION', width: 200 },
    {
      title: 'VALUE IN INR',
      dataIndex: 'fob_value',
      key: 'fob_value',
      header: 'VALUE IN INR',
      align: 'right',
      width: 155,
      render: (text, record) => {
        return formatToTwoDecimals(text)
      }
    },
    { title: 'BRC NO.', dataIndex: 'brc_no', key: 'brc_no', header: 'BRC NO.', width: 115 },
    {
      title: 'DATE OF REALISATION',
      dataIndex: 'date_of_realization',
      key: 'date_of_realization',
      header: 'DATE OF REALISATION',
      width: 195,
      render: (text, record) => record.date_of_realization ? formatDateToDDMMYY(record.date_of_realization) : ''
    },
    {
      title: 'VALUE', dataIndex: 'realised_value', key: 'realised_value', header: 'VALUE', align: 'right', width: 90,
      render: (text, record) => {
        return formatToTwoDecimals(text)
      }
    },
    { title: 'FCC', dataIndex: 'currency', key: 'currency', header: 'FCC', width: 80 },
    { title: 'PORT/CURRENCY DIFFERENCE', dataIndex: 'port_currency_difference', key: 'port_currency_difference', header: 'PORT/CURRENCY DIFFERENCE', width: 245 },
    {
      title: 'VALUE DIFF', dataIndex: 'value_difference', key: 'value_difference', header: 'VALUE DIFF', align: 'right', width: 120,
      render: (text, record) => {
        return formatToTwoDecimals(text)
      }
    },
    { title: 'RELEASED IN (DAYS)', dataIndex: 'payment_released_in_days', key: 'payment_released_in_days', header: 'RELEASED IN (DAYS)', align: 'right', width: 205 },
    { title: 'OBSERVATION', dataIndex: 'brc_remark', key: 'brc_remark', header: 'OBSERVATION', width: 135 },
    { title: 'FINAL REMARK', dataIndex: 'final_remark', key: 'final_remark', header: 'FINAL REMARK', width: 145 },
    { title: 'TEXTBOX 1', dataIndex: 'param1', key: 'param1', header: 'TEXTBOX 1', width: 125 },
    { title: 'TEXTBOX 2', dataIndex: 'param2', key: 'param2', header: 'TEXTBOX 2', width: 125 },
    { title: 'TEXTBOX 3', dataIndex: 'param3', key: 'param3', header: 'TEXTBOX 3', width: 125 },
    { title: 'TEXTBOX 4', dataIndex: 'param4', key: 'param4', header: 'TEXTBOX 4', width: 125 },
    { title: 'EGM No', dataIndex: 'egm_no', key: 'egm_no', header: 'EGM No', width: 130 },
    {
      title: 'EGM Dtd.', dataIndex: 'egm_date', key: 'egm_date', header: 'EGM Dtd.', width: 120,
      render: (text, record) => record.egm_date ? formatDateToDDMMYY(record.egm_date) : ''
    },
    { title: 'EGM Remarks', dataIndex: 'egm_remarks', key: 'egm_remarks', header: 'EGM Remarks', width: 140 },
  ];

  const { visibleKeys, setVisibleKeys, filteredColumns, resetToDefault } =
    useColumnVisibility('EPCGReport', columns);

  const dashboardReportSummaryColumns = [
    { title: 'EPCG AUTH. NO.', dataIndex: 'auth_number', key: 'auth_number', header: 'EPCG AUTH. NO.', width: 180 },
    { title: 'FY', dataIndex: 'fy', key: 'fy', header: 'FY', width: 80 },
    { title: 'EPCG AUTH DT.', dataIndex: 'date', key: 'date', header: 'EPCG AUTH DT.', width: 170 },
    // { title: 'HSN CODE', dataIndex: 'hs_cd', key: 'hs_cd', header: 'HSN CODE' },
    {
      title: 'EO-VALUE IN INR', dataIndex: 'eo_fcc', key: 'eo_fcc', header: 'EO-VALUE IN INR', align: 'right', width: 170,
      render: (text, record) => {
        return formatToTwoDecimals(text)
      }
    },
    {
      title: 'EO-FULFILLED VALUE', dataIndex: 'eo_fulfilled_val', key: 'eo_fulfilled_val', header: 'EO-FULFILLED VALUE', align: 'right', width: 195,
      render: (text, record) => {
        return formatToTwoDecimals(text)
      }
    },
    {
      title: 'EO-FULFILLED REALISED VALUE', dataIndex: 'eo_fulfilled_realised_val', key: 'eo_fulfilled_realised_val', header: 'EO-FULFILLED REALISED VALUE', align: 'right', width: 285,
      render: (text, record) => {
        return formatToTwoDecimals(text)
      }
    },
    {
      title: 'BAL. EO AS PER SHB', dataIndex: 'bal_eo_shb', key: 'bal_eo_shb', header: 'BAL. EO AS PER SHB', align: 'right', width: 205,
      render: (text, record) => {
        return formatToTwoDecimals(text)
      }
    },
    {
      title: 'BAL. EO AS PER BRC', dataIndex: 'bal_eo_brc', key: 'bal_eo_brc', header: 'BAL. EO AS PER BRC', align: 'right', width: 205,
      render: (text, record) => {
        return formatToTwoDecimals(text)
      }
    },
    { title: 'BAL. EOP', dataIndex: 'bal_eop', key: 'bal_eop', header: 'BAL. EOP', align: 'right', width: 110 },
    // { title: 'Remark', dataIndex: 'brc_remark', key: 'brc_remark', header: 'Remark' },
    {
      title: 'FINAL REMARK',
      dataIndex: 'final_remark',
      key: 'final_remark',
      header: 'FINAL REMARK',
      width: 145,
      render: (text, record) => (
        <span
          style={{ color: 'blue', cursor: 'pointer', textDecoration: 'underline' }}
          onClick={() => exportToExcel(text, record)}
        >
          {text}
        </span>
      ),
    }
  ];

  const getSummaryDashboard = async () => {
    const filters = {
      // from_date: dates?.[0] ? dayjs(dates[0]).format("YYYY-MM-DD") : null,
      // to_date: dates?.[1] ? dayjs(dates[1]).format("YYYY-MM-DD") : null,
      bill_no: billNo || null,
      br_code: selectedBranchCodes || null,
    };
    try {
      setIsSummaryLoading(true);
      const response = await getReportSummaryData('EPCG-Auth-EO', filters, selectedIEC);
      if (response?.data) {
        setSummaryDashboardData(response.data);

        // Extract `epcg_no` and return unique values
        const uniqueNos = [...new Set(response.data.map(item => item.auth_number))];
        let i = 0;
        const options = uniqueNos.map(item => ({
          value: `${item}`,
          label: `${item}`,
          key: i++,
        }));
        // setBillNoOptions(options)

      } else {
        console.log('getSummaryDashboard :: no response');
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsSummaryLoading(false);
    }
  };

  const fetchData = async () => {
    const filters = {
      // from_date: dates?.[0] ? dayjs(dates[0]).format("YYYY-MM-DD") : null,
      // to_date: dates?.[1] ? dayjs(dates[1]).format("YYYY-MM-DD") : null,
      bill_no: billNo || null,
      br_code: selectedBranchCodes || null,
    };
    try {
      const response = await iecWiseEPCGDetailsService(selectedIEC, filters);
      setJsonData(response);
    } catch (error) {
      setError('Error fetching data. Please try again later.');
      console.error('Error fetching data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch license numbers from the reference-data API
  const fetchLicenseNumbers = async () => {
    if (!selectedIEC) return;

    try {
      const data = await getReferenceDataService(selectedIEC, 'EPCG');

      // Transform API response to dropdown options
      const options = data.map((item, index) => ({
        value: item.license_number,
        label: item.license_number,
        key: index,
      }));

      setBillNoOptions(options);
    } catch (error) {
      console.error('Error fetching license numbers:', error);
      message.error('Failed to load license numbers');
    }
  };

  useEffect(() => {
    if (userRole === 'superadmin' || userRole === 'group_head') {
      getAdminIEC();
    }
    if (userRole === 'admin' || userRole === 'user') {
      fetchData();
      getSummaryDashboard();
      fetchLicenseNumbers(); // Fetch license numbers on component load
    }
    if (selectedIEC) {
      getBRCorelationService(selectedIEC).then((response) => {
        setBranchCodeNames(response);
      });
    }
  }, []);

  // Fetch license numbers when IEC changes
  useEffect(() => {
    if (selectedIEC) {
      fetchLicenseNumbers();
      getBRCorelationService(selectedIEC).then((response) => {
        setBranchCodeNames(response);
      });
    }
  }, [selectedIEC]);


  const preprocessData = (data) => {
    return data.map((record) => {
      if (record.sb_date) {
      } else {
        // record.FY = '';
        record.sb_date = '';
      }
      return record;
    });
  };

  const formatDateToDDMMYY = (dateString) => {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return '';
    }
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    return `${day}/${month}/${year}`;
  };

  const getDateOnly = (dateString) => {
    if (dateString && dateString.length !== 0) {
      const date = new Date(dateString);
      return date;
    }
    else {
      return '';
    }
  }

  const handleDateChange = (value) => {
    setDates(value || []);
    if (value && value.length === 2) {
      const from_date = value[0];
      const to_date = value[1];

      // Validate that both dates are selected
      if (!from_date || !to_date) {
        setIsValid(false);
        return;
      }

      // Validate that the duration is within 1 year
      const oneYearLater = from_date.add(1, 'year');
      if (to_date.isAfter(oneYearLater)) {
        message.error("The selected duration should not exceed 1 year.");
        setIsValid(false);
        return;
      }

      setIsValid(true);
    } else {
      setIsValid(false);
    }
  };

  const handleSubmit = () => {
    if (!selectedIEC) {
      message.error("Please select an IEC");
      return;
    }
    // if (!selectedBillNo) {
    //   message.error("Please select an Auth No");
    //   return;
    // }

    getSummaryDashboard();
    fetchData();
    // if (dates.length === 2 && isValid) {
    //   getSummaryDashboard();
    //   fetchData();
    // }
    // else {
    //   message.error("Please select a valid date range.");
    // }
  };

  const getAdminIEC = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await getAllAdminIECsService(token);

      if (response?.data) {
        const options = response.data.map((item, index) => ({
          value: `${item.iec_no};${item.fullname}`,
          label: `${item.iec_no} (${item.fullname})`,
          key: index,
        }));
        setIECOptions(options);
      }
    } catch (err) {
      message.error("Failed to load IEC list");
    }
  };

  const handleIECSelection = (val) => {
    if (val) {
      const [iec_no, iec_name] = val.split(";");
      setSelectedIEC(iec_no);
      setSelectedIECName(iec_name);
    } else {
      setSelectedIEC(null);
      setSelectedIECName('');
    }
  };


  const handleBillNoSelection = (val) => {
    if (val) {
      setSelectedBillNo(val);
      setBillNo(val);
    } else {
      setSelectedBillNo("");  //Reset selected value
      setBillNo("");
    }
  };


  const handleBranchCodeSelection = (values) => {
    setSelectedBranchCodes(values);
  };

  const exportToExcel = async (filterRemark = null, record = null) => {
    let workbook = new ExcelJS.Workbook();
    let summaryWorksheet = workbook.addWorksheet('Summary');
    let worksheet = workbook.addWorksheet('Data');

    let data = jsonData;
    let summaryData = summaryDashboardData;

    // Apply filter if remark is clicked
    if (record) {
      // Add any keys here that are aggregated in the summary (e.g., amounts/totals) 
      // so they are not used for strict matching against raw data rows.
      const ignoreKeys = ['eo_fcc', 'eo_fulfilled_val', 'eo_fulfilled_realised_val', 'bal_eo_shb', 'bal_eo_brc', 'bal_eop'];

      // Generic filter: match all keys present in both the record and the item
      const isMatch = (item, searchRecord) => {
        for (let key in searchRecord) {
          if (key === 'key' || ignoreKeys.includes(key)) continue; // Skip React 'key' prop and aggregated keys
          if (item.hasOwnProperty(key)) {
            if (item[key] !== searchRecord[key]) {
              return false;
            }
          }
        }
        return true;
      };

      data = data.filter(item => isMatch(item, record));
      summaryData = summaryData.filter(item => isMatch(item, record));
    } else if (filterRemark && typeof filterRemark === 'string') {
      data = data.filter(item => item.final_remark === filterRemark);
      summaryData = summaryData.filter(item => item.final_remark === filterRemark);
    }

    // Define columns for Excel export
    summaryWorksheet.columns = filteredSummaryColumns.map(col => ({
      header: col.header || col.title,
      key: col.dataIndex,
      width: 20
    }));
    // Apply styles to the header row (Row 1)
    summaryWorksheet.getRow(1).eachCell((cell) => {
      cell.font = { bold: true }; // Bold text
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFC000' }, // Yellow background
      };
      cell.alignment = { vertical: 'middle' }; // Center align
    });

    summaryData.forEach((obj) => {
      summaryWorksheet.addRow({
        ...obj,
        date: getDateOnly(obj.date)
      })
    })

    worksheet.columns = filteredColumns.map(col => ({
      header: col.header || col.title,
      key: col.dataIndex,
      width: 20
    }));
    // Apply styles to the header row (Row 1)
    worksheet.getRow(1).eachCell((cell) => {
      cell.font = { bold: true }; // Bold text
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFC000' }, // Yellow background
      };
      cell.alignment = { vertical: 'middle' }; // Center align
    });

    data.forEach((obj) => {
      worksheet.addRow({
        ...obj,
        date: getDateOnly(obj.date),
        sb_date: getDateOnly(obj.sb_date),
        leo_date: getDateOnly(obj.leo_date),
        date_of_realization: getDateOnly(obj.date_of_realization),
        egm_date: getDateOnly(obj.egm_date),
        eo_fcc: obj.eo_fcc ? parseFloat(obj.eo_fcc) : '',
        fob_value: obj.fob_value ? parseFloat(obj.fob_value) : '',
        realised_value: obj.realised_value ? parseFloat(obj.realised_value) : '',
        value_difference: obj.value_difference ? parseFloat(obj.value_difference) : '',
      })

    });

    // Generate Excel file
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

    // Use FileSaver to save the file
    const fileNameSuffix = filterRemark && typeof filterRemark === 'string' ? `_${filterRemark}` : '';
    saveAs(blob, `Lucrative's CIP DSS - EPCG Authorisation EO as on ${getTodaysDate()} of ${selectedIEC}${fileNameSuffix}.xlsx`);
  };

  const filteredSummaryColumns = dashboardReportSummaryColumns.filter((col) => {
    const key = String(col.dataIndex || col.key);
    const isDefinedInDetail = columns.some((c) => String(c.dataIndex || c.key) === key);
    return isDefinedInDetail ? visibleKeys.includes(key) : true;
  });

  return (
    <Fragment>
      <style>{tableStyles}</style>
      <Row>
        <Col sm="12">
          <Card>
            <Card.Header className="d-flex justify-content-between">
              <div className="header-title">
                <h4 className="card-title">
                  Lucrative's CIP DSS - EPCG Authorisation EO as on {formattedDate} of {selectedIECName}
                </h4>
              </div>
              <div className="d-flex align-items-center gap-2">
                <ColumnSelector
                  allColumns={columns}
                  visibleKeys={visibleKeys}
                  setVisibleKeys={setVisibleKeys}
                  onReset={resetToDefault}
                />
                <button
                  className="btn btn-primary cip-btn-submit"
                  disabled={isLoading || error}
                  onClick={exportToExcel}
                >
                  Export to Excel
                </button>
              </div>
            </Card.Header>
            <Card.Body className="d-flex justify-content-start align-items-center gap-2 ps-4">
              {(userRole === 'superadmin' || userRole === 'group_head') && (
                <Select
                  showSearch
                  placeholder="Select IEC"
                  onChange={handleIECSelection}
                  allowClear
                  options={IECOptions}
                  style={{ width: '300px' }}
                />
              )}
              {/* <DatePicker.RangePicker value={dates} onChange={handleDateChange}
                allowClear
              /> */}
              <Select
                showSearch
                placeholder="Select Auth No"
                // value={selectedBillNo} // Controlled by state
                allowClear
                style={{ width: 250 }}
                filterOption={(input, option) =>
                  (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                }
                onChange={handleBillNoSelection} // Update state when an option is selected
                options={billNoOptions} // Dynamically populated options
              />
              <Select
                mode="multiple"
                allowClear
                style={{ width: 250 }}
                placeholder="Select Branch Code"
                onChange={handleBranchCodeSelection}
                options={branchCodeNames.map((item) => ({
                  value: item.branch_code,
                  label: `${item.branch_code} / ${item.plant_name} (${item.business_unit})`,
                }))}
              />
              <Button type="primary" className="cip-btn-submit"
                onClick={handleSubmit}>
                Submit
              </Button>
            </Card.Body>
            <Card.Body className="px-0 report-page">
              {error && (
                <div className="text-center my-4">
                  <Alert message="Error" description={error} type="error" showIcon />
                </div>
              )}
              {/* <Spin spinning={isSummaryLoading} tip="Loading summary..."> */}
              <Table
                className="epcg-summary-table"
                columns={filteredSummaryColumns}
                dataSource={summaryDashboardData}
                pagination={false}
                loading={isSummaryLoading}
                bordered
                scroll={{ x: 'max-content' }}
                
              />
              {/* </Spin> */}
              <hr />
              {/* <Spin spinning={isLoading} tip="Loading EPCG data..."> */}
              <Table
                className="epcg-table"
                bordered
                columns={filteredColumns}
                dataSource={jsonData}
                loading={isLoading}
                pagination={{ showTotal: (total) => `Total ${total} records`, showSizeChanger: true }}
                scroll={{ x: 'max-content' }}
                
              />
              {/* </Spin> */}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Fragment>
  );
};

export default EPCGReportComponent;
